import { zodResolver } from '@hookform/resolvers/zod'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import ReactQuill from 'react-quill'
import { useUpdateContactStateMutation } from '../../../../features/contactsApi'
import { useFindAllCustomMailsQuery, useFindOneCustomMailQuery, useSendCustomMailMutation } from '../../../../features/mailsApi'
import { addBannerWithTimeout } from '../../../lib/centralizedBanner/slice'
import { ContactDTO, ContactType } from '../../../lib/filters/dto'
import { useAppDispatch, useAppSelector } from '../../../lib/redux/hooks'
import { OptionType } from '../../../lib/types'
import { colors } from '../../../ui/colors'
import { PlusIcon } from '../../../ui/icons/plus'
import Button from '../../../ui/molecules/button'
import Tiptap from '../../../ui/molecules/editorInput/tiptap'
import Input from '../../../ui/molecules/input/Input'
import { SelectCustom } from '../../../ui/molecules/select'
import { CompanyDTO } from "../../database/companies/dto"
import { PersonDTO } from "../../database/people/dto"
import { updateSendEmails } from '../../targetList/contacts/slice'
import { ContactStateEnum, TargetListDTO } from '../../targetList/dto'
import { ProjectDTO } from '../dto'
import { VariablesBody } from './dto'
import { setSelectedTemplateId, setSendEmailModalOpen } from './slice'
import { translateVariables, variablesMap } from './utils'
import { SendEmailFormValues, sendEmailSchema } from './validation'

interface Props {
    project: ProjectDTO
    name: string
    contactType: ContactType
    company?: CompanyDTO
    employees?: PersonDTO[]
    person?: PersonDTO
    targetList: TargetListDTO
    contactId: string
    contact: ContactDTO<ContactType>

    listIndex: number;
}

export function SendEmailModal({ project, name, contactType, company, employees, person, listIndex, targetList, contactId, contact }: Props) {
    const { data: templates } = useFindAllCustomMailsQuery({ projectId: project?.id }, { skip: !project?.id })
    const [mailObject, setMailObject] = useState('')
    const [mailContent, setMailContent] = useState('')

    const mailsState = useAppSelector(state => state.mails)
    const dispatch = useAppDispatch()

    const [other, setOther] = useState(false)

    const { data: templateDetails } = useFindOneCustomMailQuery(mailsState.selectedTemplateId!, { skip: !project?.id || !mailsState.selectedTemplateId })

    const quillRef = useRef<ReactQuill>(null)

    const form = useForm<SendEmailFormValues>({
        resolver: zodResolver(sendEmailSchema),
    })

    const mailTo = form.watch('to');

    const [updateContactState] = useUpdateContactStateMutation();
    const [sendCustomMail, { isSuccess: isSendCustomMailSuccess }] = useSendCustomMailMutation();

    const handleSendCustomMail = async () => {
        dispatch(setSelectedTemplateId(undefined))
        dispatch(setSendEmailModalOpen(false))
        if (other)
            dispatch(updateSendEmails({
                contactId: contactId, sendEmails: [mailTo]
            }))
        setOther(false)
        try {
            await sendCustomMail({ customMailId: templateDetails?.id!, request: form.getValues(), targetId: targetList.id }).unwrap();
            dispatch(addBannerWithTimeout({
                variant: "success",
                message: "Email inviata con successo.",
            }))
        } catch (error) {
            dispatch(addBannerWithTimeout({
                variant: "error",
                message: "Errore durante l'invio dell'email.",
            }))
        }
    }

    useEffect(() => {
        if (isSendCustomMailSuccess) {
            updateContactState({
                targetId: targetList.id,
                contactId: contactId!,
                outcome: ContactStateEnum.TORECALL,
                outcomeInfo: contactType === ContactType.PERSON ? 'Mail diretta inviata' : company?.emails?.includes(mailTo) ? 'Mail generica inviata' : 'Mail diretta inviata',
                contactType: contactType === ContactType.PERSON ? ContactType.PERSON : ContactType.COMPANY
            }).then((response) => {
                const res = response as unknown as { error: { status: number, data: { errors: { message: string }[] } } };
                if (response?.data?.success === true)
                    dispatch(addBannerWithTimeout({
                        variant: "success",
                        message: "Stato del contatto aggiornato con successo.",
                    }));
                else {
                    let message = "Si è verificato un errore durante l'aggiornamento dello stato del contatto.";
                    const errors = res?.error?.data?.errors ?? [];
                    if (errors?.some(e => e.message.includes("has already reached the maximum number of calls"))) {
                        message = "Il contatto ha già raggiunto il numero massimo di chiamate consentite.";
                    } else if (errors?.some(e => e.message.includes("Contact ID is required"))) {
                        message = "Non è possibile prendere il Lead perché non ancora presente in Lista Target. Riprova domani.";
                    } else if (errors?.some(e => e.message.includes("Invalid transition from DRAFT to DRAFT"))) {
                        message = "Il contatto è già nello stato 'Da Lavorare'";
                    }
                    dispatch(addBannerWithTimeout({
                        variant: "error",
                        message: message,
                    }));
                }
            })
        }
    }, [isSendCustomMailSuccess])

    const findValuesMap = new Map<string, string>([
        ['teamLeaderName', project && project.teamLeader ? (project.teamLeader.name + " " + project.teamLeader.surname) : ''],
        ['customerCompanyName', project && project.customer ? project.customer.companyName ? project.customer.companyName : '' : ''],
        ['customerCompanyWebsite', project && project.customer ? project.customer.companyWebsite ? project.customer.companyWebsite : '' : ''],
        //TODO SEE IF SAME ['addressee', companyState.findCompanyResponse && targetListState.editTargetListTabValue === 2 ? companyState.findCompanyResponse.name : targetListState.editTargetListTabValue === 1 && peopleState.findPersonResponse ? peopleState.findPersonResponse.name : '']
        ['addressee', name]
    ])

    let variableOptions: ReactNode[] = []
    variablesMap.forEach((key, item) => {
        variableOptions.push(
            <Button
                onClick={() => {
                    if (quillRef.current) {
                        const editor = quillRef.current.getEditor();
                        const range = editor.getSelection();

                        if (range) {
                            editor.insertText(range.index, "[" + item + "]");
                            // form.setValue('body', editor.root.innerHTML)
                        }
                    }
                }}
                label={item}
                size={'sm'}
                iconPosition={'left'}
                variant={'link'}
                color={'gray'}
            />
        );
    });
    let options: OptionType[] = []
    if (contactType === ContactType.COMPANY) {
        company?.emails?.forEach(item => options.push({ label: item, value: item }))
        employees?.forEach(item => item.emails?.forEach(elem => options.push({ label: elem, value: elem })))
        contact?.sendEmails?.forEach(item => options.push({ label: item, value: item }))
    }
    if (contactType === ContactType.PERSON) {
        person?.emails?.forEach(item => options.push({ label: item, value: item }))
        contact?.sendEmails?.forEach(item => options.push({ label: item, value: item }))
    }

    useEffect(() => {
        if (templateDetails) {
            const regex = /{{\.Body\.(.*?)}}/g;
            let match;
            let body: VariablesBody = {}
            while ((match = regex.exec(templateDetails.templateHTML)) !== null) {
                body[match[1] as keyof VariablesBody] = findValuesMap.get(match[1]);
            }
            form.setValue('body', body)
        }
    }, [templateDetails])

    useEffect(() => {
        if (templateDetails) {
            setMailObject(templateDetails.subject)
            setMailContent(templateDetails.templateHTML)
        }
    }, [templateDetails])

    if (mailsState.openSendEmailModal)
        return (
            <div className='transition-all fixed bg-opacity-30 bg-neutral-950 backdrop-blur-sm top-0 left-0 h-full w-full flex items-center justify-center z-[100]'>
                <div className=' transition-all relative flex flex-col items-center bg-white rounded-xl opacity-100 shadow-sm w-[45%] max-h-[80%]'>
                    <div
                        onClick={() => {
                            dispatch(setSendEmailModalOpen(false));
                            setSelectedTemplateId(undefined);
                            setOther(false)
                        }}
                        className="absolute cursor-pointer rotate-45 top-[-16px] right-[-16px] border border-brandPrimary-600 h-8 w-8 rounded-full flex items-center justify-center bg-brandPrimary-50"
                    >
                        <PlusIcon color={colors.brandPrimary[600]} size={16} />
                    </div>
                    <div className="p-6 text-heading-xs font-semibold text-center">
                        Invia e-mail
                    </div>
                    <div className="px-6  w-full h-full overflow-auto flex flex-col justify-start items-start gap-6 border-t">
                        <div className='flex flex-col w-full py-6 px-4 gap-3'>
                            <div className='flex flex-col gap-3 '>
                                <SelectCustom defaultValue={mailsState.selectedTemplateId} label='Seleziona template-email' onChange={(choice) => {
                                    if (typeof choice === 'string') {
                                        dispatch(setSelectedTemplateId(choice))
                                    }
                                }}
                                    placeholder={'Seleziona il template'} menuPortalTargetIsNotBody
                                    noOptionsMessage='Non hai ancora creato alcun template custom'
                                    options={templates ? templates.data.map(item => ({ label: item.templateName, value: item.id })) : []} />
                                {
                                    // contactType === ContactType.COMPANY &&
                                    <SelectCustom defaultValue={other ? 'Altro' : mailTo} label='Seleziona destinatario' onChange={(choice) => {
                                        if (choice === 'other') {
                                            setOther(true)
                                            form.setValue('to', '')
                                            return
                                        }
                                        form.setValue('to', choice as string)
                                        setOther(false)
                                    }}
                                        placeholder={'Seleziona il destinatario'} menuPortalTargetIsNotBody
                                        noOptionsMessage='Non ci sono possibili destinatari'
                                        options={
                                            [...options, { label: 'Altro', value: 'other' }]
                                        }
                                    />
                                }

                            </div>
                            {contactType === ContactType.PERSON &&
                                <div className='flex flex-col gap-3 '>
                                    <Input label="Azienda associata" disabled value={person ? (person.companyName?.length > 0 ? person.companyName : 'Nessuna azienda associata') : 'N/A'} placeholder="Azienda associata" />
                                </div>
                            }
                            {other && <div className='flex flex-col gap-3 '>
                                <Input label='Destinatario' value={mailTo} onChange={(e) => form.setValue('to', e.target.value)} placeholder="Specificare l'indirizzo email dell'utente" />
                            </div>}
                            <div className='flex flex-col gap-3'>
                                <Input disabled label='Oggetto' value={mailObject} onChange={(e) => setMailObject(e.target.value)} placeholder="Specificare l'oggetto della mail" />
                            </div>
                            <div className='flex flex-col gap-11'>
                                <Tiptap content={translateVariables(mailContent)} disabled={true} />
                                {/* <EditorInput
                                    disabled
                                    label='Contenuto della mail'
                                    value={translateVariables(mailContent)}
                                    placeholder="Descrivere il contenuto dell'e-mail"
                                    id='input-editor-settings-email-edit'
                                    key='input-editor-settings-email-edit'
                                    onChangeText={(e) => {
                                        const parser = new DOMParser();
                                        const doc = parser.parseFromString(e, "text/html");

                                        const links = doc.querySelectorAll("a");

                                        if (links.length > 0) {
                                            links.forEach((link) => {
                                                let href = link.getAttribute("href");

                                                if (href && !href.startsWith("http://") && !href.startsWith("https://")) {
                                                    link.setAttribute("href", `https://${href}`);
                                                }
                                            });
                                            setMailContent(doc.body.innerHTML)
                                        } else {
                                            setMailContent(e)
                                        }
                                    }}
                                    ref={quillRef}
                                /> */}
                            </div>
                        </div>
                        <div className=' flex justify-end items-center w-full border-t gap-2 py-4 px-6 border-neutral-200 mt-4'>
                            <Button
                                disabled={!templateDetails || !mailTo || mailTo === ''}
                                onClick={() => {
                                    form.handleSubmit(handleSendCustomMail)()
                                }}
                                label='Invia e-mail' size={'sm'} iconPosition={'left'} variant={'solid'} color={'green'} />
                        </div>
                    </div>
                </div>
            </div>
        )
    else return <></>
}